/**
 * The single source of truth for app navigation.
 *
 * Sidebar (desktop) and MobileSidebar (the "More" drawer) both render from
 * these lists. They used to keep their own hand-maintained copies, which drifted
 * — seven pages, including the ISP Dashboard, existed only on desktop. Add a
 * page here once and it appears on both.
 */
import {
  LayoutDashboard, ClipboardCheck, Calculator, Receipt, Wrench, ClipboardList,
  Inbox, CalendarDays, CalendarClock, CalendarRange, HardHat, Users, Clock, Calendar, Truck, PackageCheck,
  FileText, Archive, DollarSign, TrendingUp, Activity, Building2, Package,
  Target, BookOpen, BarChart3, ShoppingCart, Radio, MessageSquare, MapPin, ListChecks,
  Sparkles, View, Ruler, Video, Mail, Briefcase, CalendarCheck,
  Settings, CreditCard, UserCog, Boxes, LifeBuoy, Hammer, Wallet,
} from 'lucide-react'
import type { PageSlug } from '@/hooks/use-permissions'

export interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Page slug for permission gating. Role defaults live on the slug in
   *  PAGE_TIER, so an admin can grant one page to one member without
   *  changing their role. */
  page?: PageSlug
}

export const mainNavItems: NavItem[] = [
  { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, page: 'dashboard' },
  { title: 'Inspections', href: '/inspections', icon: ClipboardCheck, page: 'inspections' },
  { title: 'Service Tickets', href: '/tickets', icon: Wrench, page: 'service_tickets' },
]

export const cmmsNavItems: NavItem[] = [
  { title: 'Work Orders', href: '/work-orders', icon: ClipboardList, page: 'work_orders' },
  { title: 'Service Intake', href: '/intake', icon: Inbox, page: 'service_intake' },
  { title: 'Scheduling', href: '/scheduling', icon: CalendarDays, page: 'scheduling' },
  { title: 'Sub Calendar', href: '/sub-calendar', icon: CalendarClock, page: 'sub_calendar' },
  { title: 'Crew Calendar', href: '/crew-calendar', icon: CalendarRange, page: 'crew_calendar' },
  { title: 'My Crew Week', href: '/portal/crew', icon: HardHat, page: 'crew_week' },
  { title: 'Crew Dashboard', href: '/crew-dashboard', icon: Users, page: 'crew_dashboard' },
  { title: 'PM Schedules', href: '/maintenance', icon: Calendar, page: 'pm_schedules' },
  { title: 'Vendors', href: '/vendors', icon: Truck, page: 'vendors' },
  { title: 'RFQs', href: '/rfqs', icon: FileText, page: 'rfqs' },
]

/** Slotted into CMMS at render time — only when the org has archive boards. */
export const archivesCmmsItem: NavItem = {
  title: 'Archived Work Orders',
  href: '/work-orders/archive',
  icon: Archive,
  page: 'wo_archive',
}

/** Everything money-related, grouped. Permission slugs are unchanged — role
 *  grants reference them — only titles and the billing route moved. */
export const financeNavItems: NavItem[] = [
  { title: 'Billing / Revenue', href: '/finance/billing', icon: DollarSign, page: 'finance' },
  { title: 'Invoices', href: '/invoices', icon: FileText, page: 'invoices' },
  { title: 'Estimates', href: '/estimates', icon: Calculator, page: 'estimates' },
  { title: 'Receipts', href: '/finance/receipts', icon: Receipt, page: 'receipts' },
  { title: 'Receipt Inbox', href: '/receipts-inbox', icon: Inbox, page: 'receipts_inbox' },
  { title: 'Timesheets', href: '/timesheets', icon: Clock, page: 'timesheets' },
  { title: 'FM Budget', href: '/budget', icon: Wallet, page: 'fm_budget' },
  { title: 'Capital Plan', href: '/capital-plan', icon: TrendingUp, page: 'capital_plan' },
]

export const planningNavItems: NavItem[] = [
  { title: 'Facility Health', href: '/facility-health', icon: Activity, page: 'facility_health' },
]

export const managementNavItems: NavItem[] = [
  { title: 'Clients', href: '/clients', icon: Users, page: 'clients' },
  { title: 'Equipment', href: '/equipment', icon: Building2, page: 'equipment' },
  { title: 'Parts Inventory', href: '/inventory', icon: Package, page: 'parts_inventory' },
  { title: 'Forms', href: '/forms', icon: ClipboardCheck, page: 'forms' },
  { title: 'Goals', href: '/goals', icon: Target, page: 'goals' },
  { title: 'Knowledge Base', href: '/knowledge', icon: BookOpen, page: 'knowledge_base' },
  { title: 'Reports', href: '/reports', icon: FileText, page: 'reports' },
  { title: 'Scorecard', href: '/scorecard', icon: BarChart3, page: 'scorecard' },
  { title: 'Reorder Queue', href: '/reorder-queue', icon: ShoppingCart, page: 'reorder_queue' },
  { title: 'Purchasing', href: '/purchasing', icon: ShoppingCart, page: 'reorder_queue' },
  { title: 'Deliveries', href: '/deliveries', icon: PackageCheck, page: 'deliveries' },
  { title: 'Power Tools', href: '/tools', icon: Hammer, page: 'power_tools' },
  { title: 'Gate Board', href: '/gates', icon: ListChecks, page: 'gate_board' },
  { title: 'Program Report', href: '/program', icon: Target, page: 'program_report' },
  { title: 'Close-out Review', href: '/closeout-review', icon: CalendarCheck, page: 'closeout_review' },
  { title: 'ISP Dashboard', href: '/dispatch', icon: Radio, page: 'dispatch' },
  { title: 'Messages', href: '/messages', icon: MessageSquare, page: 'messaging' },
]

export const salesNavItems: NavItem[] = [
  { title: 'Prospect Sites', href: '/prospects', icon: MapPin, page: 'prospect_sites' },
]

export const toolsNavItems: NavItem[] = [
  { title: 'Ask Opsyri', href: '/assistant', icon: Sparkles, page: 'ask_opsyri' },
  { title: '360° Tours', href: '/tours', icon: View, page: '360_tours' },
  { title: 'Blueprint Takeoff', href: '/takeoffs', icon: Ruler, page: 'takeoffs' },
  { title: 'Screen Recorder', href: '/recordings', icon: Video, page: 'recordings' },
]

/**
 * The pinned footer cluster, below the scrolling nav.
 *
 * This list is why the drift the file header describes came back: the section
 * lists were unified here, but both sidebars kept hand-written copies of their
 * footer links. Desktop grew Team, Roles, Workflow and Billing; the mobile
 * drawer never did, so an admin on a phone had no route to any of them.
 *
 * Help carries no `page` — it is ungated for everyone.
 */
export const settingsNavItems: NavItem[] = [
  { title: 'Help & How-To', href: '/help', icon: LifeBuoy },
  { title: 'Team', href: '/settings/team', icon: Users, page: 'team_management' },
  { title: 'Roles', href: '/settings/roles', icon: UserCog, page: 'roles' },
  { title: 'Workflow', href: '/settings/workflow', icon: Boxes, page: 'workflow' },
  { title: 'Settings', href: '/settings', icon: Settings, page: 'settings' },
  { title: 'Billing', href: '/settings/billing', icon: CreditCard, page: 'billing' },
]

export const vendorPortalNavItems: NavItem[] = [
  { title: 'Dashboard', href: '/portal/vendor', icon: LayoutDashboard },
  { title: 'Projects', href: '/portal/vendor/projects', icon: Briefcase },
  { title: 'My RFQs', href: '/portal/vendor/rfqs', icon: FileText },
  { title: 'Inbox', href: '/portal/vendor/inbox', icon: Mail },
]

export const clientPortalNavItems: NavItem[] = [
  { title: 'Dashboard', href: '/portal/client', icon: LayoutDashboard },
  { title: 'Locations', href: '/portal/client/locations', icon: Building2 },
  { title: 'Inspections', href: '/portal/client/inspections', icon: ClipboardCheck },
  { title: '360° Tours', href: '/portal/client/tours', icon: View },
]
